import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type RefetchOptions,
  type RefetchQueryFilters,
  type QueryObserverResult
} from '@tanstack/react-query'
import {
  addAccount,
  type AddAccountReq,
  type AddAccountResp,
  type ClientAccountsResp,
  deleteClientAccount,
  getClientAccountPlatform,
  getClientAccounts,
  type Platforms,
  reconnectAccount,
  type ReconnectAccountReq,
  updateRecommendationsForAccount,
  type UpdateRecommendationsForAccountReq
} from 'shared/api/accounts'
import { type ClientError } from 'shared/api/fetch'
import { useInfiniteQuery } from '@tanstack/react-query'
import { getExternalAccounts, type ExternalAccountsResp } from 'shared/api/credentials'
import { type CredentialsResp, getUserCredentials } from 'shared/api/users'
import { useCurrentClient } from 'shared/context/ClientContext'
import { isNilOrEmpty } from 'shared/utils'
import { THIRD_PARTY_PROVIDER, type ThirdPartyProviders } from 'shared/thirdParty'
import { retryDelayFn, retryFn } from 'shared/reactQuery'

export const queryKeys = {
  clientAccounts: 'clientAccounts',
  externalAccounts: 'externalAccounts',
  clientCredentials: 'clientCredentials'
} as const

export type QueryClientsRefetch = (
  options?: RefetchOptions & RefetchQueryFilters
) => Promise<QueryObserverResult<ClientAccountsResp[], ClientError>>

export const useQueryClientAccounts = (
  clientId: number,
  enabled = true,
  refetchOnWindowFocus?: boolean
): UseQueryResult<ClientAccountsResp[], ClientError> => {
  return useQuery({
    queryKey: [queryKeys.clientAccounts, clientId],
    queryFn: async () => await getClientAccounts(clientId),
    enabled: Boolean(clientId) && enabled,
    ...(refetchOnWindowFocus === true ? { refetchOnWindowFocus: true, staleTime: 0 } : {})
  })
}

export const useQueryClientAccountPlatform = (
  clientId: number,
  platform: Platforms
): UseQueryResult<ClientAccountsResp[], ClientError> => {
  return useQuery({
    queryKey: [queryKeys.clientAccounts, clientId, platform],
    queryFn: async () => await getClientAccountPlatform(clientId, platform),
    enabled: Boolean(clientId),
    refetchOnWindowFocus: false
  })
}

export const useDeleteClientAccounts = (
  clientId: number
): UseMutationResult<ClientAccountsResp, ClientError, number> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (accountId: number) => await deleteClientAccount(clientId, accountId),
    onSuccess: async (): Promise<void> => {
      await queryClient.invalidateQueries({ queryKey: [queryKeys.clientAccounts] })
    }
  })
}

type BulkDeleteResult = Array<{
  accountId: number
  success: boolean
  error?: ClientError
}>

export const useBulkDeleteClientAccounts = (
  clientId: number
): UseMutationResult<BulkDeleteResult, ClientError, number[]> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (accountIds: number[]): Promise<BulkDeleteResult> => {
      const deletePromises = accountIds.map(async (accountId) => {
        try {
          await deleteClientAccount(clientId, accountId)
          return { accountId, success: true }
        } catch (error) {
          return { accountId, success: false, error: error as ClientError }
        }
      })

      return await Promise.all(deletePromises)
    },
    onSuccess: async (): Promise<void> => {
      await queryClient.invalidateQueries({ queryKey: [queryKeys.clientAccounts] })
    }
  })
}

const filterFivetranCredentialsByClient = (
  clientId: number
): ((credentials: { externalUserName: string }) => boolean) => {
  const clientIdRE = new RegExp(`[^0-9]${clientId}$`)
  return ({ externalUserName }: { externalUserName: string }) => clientIdRE.test(externalUserName)
}

export const useQueryClientCredentials = (
  userId?: number,
  platform?: Platforms | ThirdPartyProviders
): UseQueryResult<CredentialsResp[], ClientError> => {
  const {
    currentClient: { id: clientId }
  } = useCurrentClient()

  return useQuery({
    queryKey: [queryKeys.clientCredentials, userId, platform ?? '', clientId],
    queryFn: async (): Promise<CredentialsResp[]> => {
      return await getUserCredentials(userId as number, platform ?? '', clientId)
    },
    enabled: userId != null && !isNilOrEmpty(platform) && platform != null,
    select: (data) =>
      platform === THIRD_PARTY_PROVIDER.FIVETRAN ? data.filter(filterFivetranCredentialsByClient(clientId)) : data
  })
}

export const useQueryExternalAccounts = (
  platform: string,
  credentialId: string,
  searchQuery: string
): UseInfiniteQueryResult<InfiniteData<ExternalAccountsResp>, ClientError> => {
  return useInfiniteQuery({
    queryKey: [queryKeys.externalAccounts, credentialId, platform, searchQuery],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      return await getExternalAccounts(platform, credentialId, pageParam, searchQuery)
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.nextCursor != null && lastPage.nextCursor !== '') {
        return lastPage.nextCursor
      }
      return undefined
    },
    enabled: credentialId !== ''
  })
}

export const useAddAccount = (): UseMutationResult<AddAccountResp, ClientError, AddAccountReq> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: AddAccountReq) => await addAccount(data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [queryKeys.clientAccounts] })
    }
  })
}

export const useReconnectAccount = (): UseMutationResult<AddAccountResp, ClientError, ReconnectAccountReq> => {
  const queryClient = useQueryClient()
  return useMutation<AddAccountResp, ClientError, ReconnectAccountReq>({
    mutationFn: reconnectAccount,
    retry: retryFn(2),
    retryDelay: retryDelayFn,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [queryKeys.clientAccounts] })
    }
  })
}

export const useUpdateRecommendationsForAccount = (): UseMutationResult<
  AddAccountResp,
  ClientError,
  UpdateRecommendationsForAccountReq
> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: updateRecommendationsForAccount,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [queryKeys.clientAccounts] })
    }
  })
}
